import React, { useState } from 'react';
import { UserPhoto, EmploymentHistory, ReferenceModal, DocumentViewer } from '../../components';
import {
  Mail, User, Calendar, Clock, Briefcase, ShieldCheck, MapPin, IdCard,
  KeyRound, Smartphone, Monitor, Check, Minus, Eye, FileText, History, Users,
} from 'lucide-react';
// Briefcase is already imported above for the Employment section.

/** How many recent shift-days the record lists before it just says how many. */
const RECENT_SHIFTS = 5;

/**
 * A staff member's record, read-only.
 *
 * Presentational and layout-agnostic: it takes an already-loaded `staff` and
 * renders it. Two hosts use it — the slide-over on the Staff list (see
 * StaffList.js) and the full page behind /manager/staffs/:id/view (StaffView.js,
 * which Recruitment still navigates to after a transfer). One implementation,
 * so a field added here shows up in both rather than in whichever one somebody
 * remembered.
 *
 * `dense` is the only difference between the two: inside a ~560px panel the
 * two-column field grid and the page's generous padding do not fit, so a dense
 * render stacks fields in one column and tightens the cards. Nothing is hidden
 * — a panel shows the same record as the page.
 */

const initials = (name) => {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';
};

/** Show only the last four digits of a bank account number. */
const maskAccount = (v) => {
  const digits = String(v || '').replace(/\D/g, '');
  if (!digits) return null;
  return digits.length <= 4 ? digits : `•••• ${digits.slice(-4)}`;
};

/** "08:02" from an ISO timestamp; null when there isn't one. */
const fmtClock = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

const minutesOfTime = (hm) => {
  const match = String(hm || '').match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return (Number(match[1]) * 60) + Number(match[2]);
};

/**
 * Paid hours for one rostered day, minus the unpaid break.
 *
 * A finish at or before the start is an overnight shift and rolls into the next
 * day. Mirrors paidHoursFor in api/utils/weekPattern.js and in the Add & Edit
 * Staff form, so the record, the form and the stored value all agree.
 */
const paidHoursFor = (entry) => {
  if (!entry?.on) return 0;
  const start = minutesOfTime(entry.start);
  const end = minutesOfTime(entry.end);
  if (start === null || end === null) return 0;
  const span = end > start ? end - start : (end + (24 * 60)) - start;
  const paid = span - (Number(entry.break_minutes) || 0);
  return paid > 0 ? Math.round((paid / 60) * 100) / 100 : 0;
};

const fmtDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

// A single label/value pair — renders nothing when the value is empty.
const Field = ({ label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div>
      <p className="text-[11px] font-semibold text-[#94A3B8] dark:text-gray-500 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-[14px] text-[#1E293B] dark:text-gray-200 font-medium break-words">{String(value)}</p>
    </div>
  );
};

// A section card; hides itself if none of its fields have values.
const Section = ({ icon: Icon, title, dense, children }) => {
  const hasContent = React.Children.toArray(children).some(Boolean);
  if (!hasContent) return null;
  return (
    <div className={`bg-white dark:bg-[#262b33] rounded-2xl border border-[#E2E8F0] dark:border-gray-800 shadow-sm ${dense ? 'p-4' : 'p-6'}`}>
      <div className={`flex items-center gap-2 ${dense ? 'mb-4' : 'mb-5'}`}>
        <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-[#2563EB]" />
        </div>
        <h3 className="text-[15px] font-bold text-[#1E293B] dark:text-gray-200">{title}</h3>
      </div>
      <div className={`grid grid-cols-1 ${dense ? 'gap-y-3.5' : 'sm:grid-cols-2 gap-x-8 gap-y-4'}`}>
        {children}
      </div>
    </div>
  );
};

const StaffDetails = ({ staff, dense = false }) => {
  // Which referee's reference is open, if any. Declared before the guard below
  // so the hook runs on every render, staff or no staff.
  const [openReferenceId, setOpenReferenceId] = useState(null);
  // Documents open in place, so the file can be read beside the record.
  const [preview, setPreview] = useState(null);

  if (!staff) return null;

  const d = staff.userDetails || {};
  const roster = staff.rosterDetails;
  // Carer = they use the mobile app. Derived from the role id by the API, with
  // the same fallback the list uses in case an older API doesn't send the flag.
  const isCarer = staff.is_carer !== undefined
    ? staff.is_carer === true
    : [3, 5].includes(Number(staff.role_id));
  const roleLabel = isCarer ? 'Carer' : 'Manager';
  // Mobile app sign-in. A carer always has it; anyone else needed it granted,
  // and is view-only in the app.
  const hasApp = isCarer || staff.app_access === true;
  // The dashboard modules this person was granted, by name. Sent by
  // getStaffById alongside menu_ids.
  const grantedMenus = Array.isArray(staff.menus) ? staff.menus : [];
  // Uploaded files, written by the Documents section of the Add/Edit Staff
  // form. Each entry is { id, category, name, expiry, file_url, file_name,
  // uploaded_at } — see models/userProfileSections.js.
  const staffDocuments = Array.isArray(d.staff_documents) ? d.staff_documents : [];
  // Referees, shaped and token-stripped by the API. Older responses (and the
  // Recruitment transfer's own payload) only carry the raw profile column, so
  // fall back to it rather than showing nothing.
  const references = Array.isArray(staff.references)
    ? staff.references
    : (Array.isArray(d.references_data) ? d.references_data : []);
  // Last 30 shift-days, newest first, as getStaffById returns them.
  const shiftHistory = Array.isArray(staff.shiftHistory) ? staff.shiftHistory : [];
  // Previous roles. The API normalises this — including rows that still hold
  // the old plain list of employer names — so it is always a list of entries.
  const employmentHistory = Array.isArray(d.previous_employers) ? d.previous_employers : [];

  /* The weekly pattern. The API always sends one — building it from the old
   * working_days / shift_start / shift_end columns for anybody saved before
   * per-day existed — so this only has to fall back when there is no roster
   * row at all. See withWeekPattern in the staff controller. */
  const weekPattern = Array.isArray(roster?.week_pattern) ? roster.week_pattern : [];
  // A domiciliary service records availability, not shifts: same rows, but no
  // unpaid break and no paid hours to show.
  const isAvailabilityPattern =
    String(roster?.pattern_mode || '').toUpperCase() === 'DOMICILIARY';
  const paidHoursPerWeek = isAvailabilityPattern
    ? 0
    : (roster?.paid_hours_per_week
      ?? Math.round(weekPattern.reduce((sum, e) => sum + paidHoursFor(e), 0) * 100) / 100);

  const nextOfKin = d.next_of_kin && typeof d.next_of_kin === 'object'
    ? [d.next_of_kin.name, d.next_of_kin.relationship, d.next_of_kin.phone].filter(Boolean).join(' · ')
    : d.next_of_kin;

  const cardPad = dense ? 'p-4' : 'p-6';

  return (
    <div className={dense ? 'space-y-4' : 'space-y-6'}>
      {/* Identity header. The panel's own header already carries the name, so
          this is skipped there — repeating it would waste the first screenful
          of a narrow panel. */}
      {!dense && (
        <div className="bg-white dark:bg-[#262b33] rounded-2xl border border-[#E2E8F0] dark:border-gray-800 shadow-sm p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <UserPhoto
              src={staff.avatar_url}
              alt={staff.name}
              className="w-16 h-16 rounded-full"
              fallback={(
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 text-[#2563EB] text-[20px] font-black flex items-center justify-center shrink-0">
                  {initials(staff.name)}
                </div>
              )}
            />
            <div className="min-w-0 flex-1">
              <h1 className="text-[22px] font-bold text-[#1E293B] dark:text-gray-100 tracking-tight">{staff.name}</h1>
              <p className="text-[13px] text-[#94A3B8] dark:text-gray-500 font-mono">{staff.user_id}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center px-3 py-1 rounded-md text-[12px] font-semibold ${
                isCarer
                  ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 border border-purple-100 dark:border-purple-900/50'
                  : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-100 dark:border-blue-900/50'
              }`}>
                {roleLabel}
              </span>
              <span className={`inline-flex items-center px-3 py-1 rounded-md text-[12px] font-semibold ${
                staff.is_active
                  ? 'bg-green-50 dark:bg-emerald-900/10 text-green-700 dark:text-emerald-400 border border-green-100 dark:border-emerald-900/50'
                  : 'bg-slate-100 dark:bg-[#31373f] text-slate-500 dark:text-gray-400 border border-slate-200 dark:border-gray-700'
              }`}>
                {staff.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Access first in the panel.
          It is the question a manager opens this record to answer — what can
          this person actually get into — so in a narrow panel it earns the top
          of the scroll rather than sitting below six sections of paperwork. On
          the wide page the original order is kept (see the second render
          below), because there the sections are scannable at a glance. */}
      {dense && (
        <AccessCard
          isCarer={isCarer}
          hasApp={hasApp}
          canLogMeds={isCarer && staff.medication_access === true}
          grantedMenus={grantedMenus}
          cardPad={cardPad}
        />
      )}

      <Section icon={Mail} title="Contact" dense={dense}>
        <Field label="Email" value={staff.email} />
        <Field label="Phone" value={staff.phone} />
        <Field label="Preferred name" value={d.preferred_name} />
      </Section>

      <Section icon={User} title="Personal Details" dense={dense}>
        <Field label="Date of birth" value={fmtDate(d.dob)} />
        <Field label="Gender" value={d.gender} />
        <Field label="Nationality" value={d.nationality} />
        <Field label="Ethnicity" value={d.ethnicity} />
        <Field label="NI number" value={d.ni_number} />
        <Field label="Disability status" value={d.disability_status} />
      </Section>

      <Section icon={MapPin} title="Address & Emergency Contact" dense={dense}>
        <Field label="Current address" value={d.current_address} />
        <Field label="Emergency contact" value={[d.emergency_contact_name, d.emergency_contact_rel].filter(Boolean).join(' · ')} />
        <Field label="Emergency phone" value={d.emergency_contact_phone} />
        <Field label="Next of kin" value={nextOfKin} />
      </Section>

      <Section icon={Briefcase} title="Employment" dense={dense}>
        <Field label="Job title" value={d.job_title} />
        <Field label="Employment type" value={d.employment_type} />
        <Field label="Contract type" value={d.contract_type} />
        <Field label="Start date" value={fmtDate(d.start_date)} />
        <Field label="Probation end" value={fmtDate(d.probation_end_date)} />
        <Field label="Line manager" value={d.line_manager} />
        {/* The numeric rate payroll uses, shown ahead of the free-text band so
            the figure that actually pays them reads first. */}
        <Field
          label="Hourly rate (payroll)"
          value={d.hourly_rate == null ? null : `${Number(d.hourly_rate).toFixed(2)} / hour`}
        />
        <Field label="Pay rate / band (note)" value={d.pay_rate} />
        <Field label="Notice period" value={d.notice_period} />
        {/* Payroll bank details — given by the candidate on their onboarding
            form, or typed on the Add Staff form. The account number is masked;
            it is only ever needed in full by the pay run. */}
        <Field label="Bank account name" value={d.bank_account_name} />
        <Field label="Sort code" value={d.bank_sort_code} />
        <Field label="Account number" value={maskAccount(d.bank_account_number)} />
      </Section>

      <Section icon={ShieldCheck} title="Compliance & Right to Work" dense={dense}>
        <Field label="DBS number" value={d.dbs_cert_number} />
        <Field label="DBS level" value={d.dbs_level} />
        <Field label="DBS issue date" value={fmtDate(d.dbs_issue_date)} />
        <Field label="RTW document" value={d.rtw_doc_type} />
        <Field label="RTW document no." value={d.rtw_doc_number} />
        <Field label="RTW expiry" value={fmtDate(d.rtw_expiry_date)} />
        <Field label="Immigration status" value={d.immigration_status} />
        <Field label="Share code" value={d.share_code} />
      </Section>

      {/* References — collected during recruitment, kept with the record.
          Read-only and permanently so: a reference is somebody else's
          statement about this person, and nothing in the organisation may edit
          it after the fact. The card opens the same modal the Recruitment step
          uses, so the reference reads identically two years after the hire. */}
      {references.length > 0 && (
        <div className={`bg-white dark:bg-[#262b33] rounded-2xl border border-[#E2E8F0] dark:border-gray-800 shadow-sm ${cardPad}`}>
          <div className={`flex items-center gap-2 ${dense ? 'mb-4' : 'mb-5'}`}>
            <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center shrink-0">
              <Users className="w-4 h-4 text-[#2563EB]" />
            </div>
            <h3 className="text-[15px] font-bold text-[#1E293B] dark:text-gray-200">
              References <span className="text-[#94A3B8] dark:text-gray-500 font-semibold">({references.length})</span>
            </h3>
          </div>
          <div className="space-y-2">
            {references.map((row) => (
              <button
                key={row.id}
                type="button"
                onClick={() => setOpenReferenceId(row.id)}
                title="Open this reference"
                className="w-full flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-xl border border-[#E2E8F0] dark:border-gray-800 bg-[#F8FAFC] dark:bg-[#20242c] text-left hover:bg-[#F1F5F9] dark:hover:bg-[#2c323b] transition-colors"
              >
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-[#1E293B] dark:text-gray-200 truncate">
                    {row.name || 'Name not given'}
                    <span className="ml-2 text-[11.5px] font-medium text-[#94A3B8] dark:text-gray-500">
                      {row.type === 'character' ? 'Character' : 'Employment'}
                    </span>
                  </p>
                  <p className="text-[12px] text-[#64748B] dark:text-gray-400 truncate">
                    {row.received
                      ? `Received${row.received_at ? ` · ${fmtDate(row.received_at)}` : ''}`
                      : row.status === 'Requested'
                        ? 'Form sent — no answer yet'
                        : 'Not requested'}
                  </p>
                </div>
                <Eye className="w-4 h-4 text-[#94A3B8] shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      <DocumentViewer file={preview} onClose={() => setPreview(null)} />

      <ReferenceModal
        isOpen={Boolean(openReferenceId)}
        onClose={() => setOpenReferenceId(null)}
        reference={references.find((row) => row.id === openReferenceId) || null}
        fmtDate={fmtDate}
      />

      {/* On the page, Access keeps its original position. */}
      {!dense && (
        <AccessCard
          isCarer={isCarer}
          hasApp={hasApp}
          canLogMeds={isCarer && staff.medication_access === true}
          grantedMenus={grantedMenus}
          cardPad={cardPad}
        />
      )}

      {/* Documents — the files uploaded against this staff member (contracts,
          DBS certificates, training certificates, proof of address…).
          Never shown on the record before, so a manager had to open the Edit
          form to find out whether a document was even on file. */}
      {staffDocuments.length > 0 && (
        <div className={`bg-white dark:bg-[#262b33] rounded-2xl border border-[#E2E8F0] dark:border-gray-800 shadow-sm ${cardPad}`}>
          <div className={`flex items-center gap-2 ${dense ? 'mb-4' : 'mb-5'}`}>
            <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center shrink-0">
              <FileText className="w-4 h-4 text-[#2563EB]" />
            </div>
            <h3 className="text-[15px] font-bold text-[#1E293B] dark:text-gray-200">
              Documents <span className="text-[#94A3B8] dark:text-gray-500 font-semibold">({staffDocuments.length})</span>
            </h3>
          </div>
          <div className="space-y-2">
            {staffDocuments.map((doc, index) => (
              <div
                key={doc.id ?? `${doc.name}-${index}`}
                className="flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-xl border border-[#E2E8F0] dark:border-gray-800 bg-[#F8FAFC] dark:bg-[#20242c]"
              >
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-[#1E293B] dark:text-gray-200 truncate">
                    {doc.name || doc.file_name || 'Untitled document'}
                  </p>
                  <p className="text-[12px] text-[#64748B] dark:text-gray-400 truncate">
                    {[
                      doc.category,
                      doc.expiry ? `review ${fmtDate(doc.expiry) || doc.expiry}` : null,
                      doc.uploaded_at ? `added ${fmtDate(doc.uploaded_at) || doc.uploaded_at}` : null,
                    ].filter(Boolean).join(' · ')}
                  </p>
                </div>
                {doc.file_url && (
                  <button
                    type="button"
                    onClick={() => setPreview({
                      url: doc.file_url,
                      name: doc.name || doc.file_name,
                      title: doc.category || 'Document',
                    })}
                    className="shrink-0 text-[12px] font-semibold text-[#2563EB] hover:underline"
                  >
                    View
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Employment history — where they worked BEFORE this job, and the gaps.
          Read-only here: it is edited on the Add & Edit Staff form, or declared
          by the candidate on their own information form. Uses the same
          component as both, so the record cannot show less than was asked for. */}
      {employmentHistory.length > 0 && (
        <div className={`bg-white dark:bg-[#262b33] rounded-2xl border border-[#E2E8F0] dark:border-gray-800 shadow-sm ${cardPad}`}>
          <div className={`flex items-center gap-2 ${dense ? 'mb-4' : 'mb-5'}`}>
            <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center shrink-0">
              <Briefcase className="w-4 h-4 text-[#2563EB]" />
            </div>
            <h3 className="text-[15px] font-bold text-[#1E293B] dark:text-gray-200">
              Employment history{' '}
              <span className="text-[#94A3B8] dark:text-gray-500 font-semibold">
                ({employmentHistory.length})
              </span>
            </h3>
          </div>
          <EmploymentHistory value={employmentHistory} readOnly />
        </div>
      )}

      <Section icon={IdCard} title="Identity Documents" dense={dense}>
        <Field label="ID document type" value={d.id_doc_type} />
        <Field label="ID document number" value={d.id_doc_number} />
        <Field label="ID verified on" value={fmtDate(d.id_verification_date)} />
        <Field label="Address document" value={d.address_doc_type} />
        <Field label="Address verified on" value={fmtDate(d.address_verification_date)} />
      </Section>

      {/* Recent shifts — already in the getStaffById response (last 30 days),
          but never surfaced on the record. Only the most recent few are shown:
          this is "has this person been working", not a timesheet. The Rota and
          Payroll screens are where the full picture lives. */}
      {shiftHistory.length > 0 && (
        <div className={`bg-white dark:bg-[#262b33] rounded-2xl border border-[#E2E8F0] dark:border-gray-800 shadow-sm ${cardPad}`}>
          <div className={`flex items-center gap-2 ${dense ? 'mb-4' : 'mb-5'}`}>
            <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center shrink-0">
              <History className="w-4 h-4 text-[#2563EB]" />
            </div>
            <h3 className="text-[15px] font-bold text-[#1E293B] dark:text-gray-200">
              Recent shifts{' '}
              <span className="text-[#94A3B8] dark:text-gray-500 font-semibold">
                ({shiftHistory.length})
              </span>
            </h3>
          </div>
          <div className="space-y-2">
            {shiftHistory.slice(0, RECENT_SHIFTS).map((shift, index) => (
              <div
                key={shift.id ?? `${shift.shift_date}-${index}`}
                className="flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-xl border border-[#E2E8F0] dark:border-gray-800 bg-[#F8FAFC] dark:bg-[#20242c]"
              >
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-[#1E293B] dark:text-gray-200 truncate">
                    {fmtDate(shift.shift_date) || shift.shift_date}
                  </p>
                  <p className="text-[12px] text-[#64748B] dark:text-gray-400 truncate">
                    {[
                      fmtClock(shift.start_time) && fmtClock(shift.end_time)
                        ? `${fmtClock(shift.start_time)} – ${fmtClock(shift.end_time)}`
                        : fmtClock(shift.start_time)
                          ? `from ${fmtClock(shift.start_time)}`
                          : null,
                      shift.break_time_minutes ? `${shift.break_time_minutes}m break` : null,
                      shift.travel_time_minutes ? `${shift.travel_time_minutes}m travel` : null,
                    ].filter(Boolean).join(' · ') || '—'}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  {shift.total_hours != null && (
                    <p className="text-[13px] font-bold text-[#1E293B] dark:text-gray-200">
                      {Number(shift.total_hours).toFixed(2)}h
                    </p>
                  )}
                  {/* An open shift is the one thing worth calling out here — it
                      means they are clocked in right now. */}
                  {shift.status === 'ongoing' && (
                    <p className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">
                      Ongoing
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
          {shiftHistory.length > RECENT_SHIFTS && (
            <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-3">
              Showing the {RECENT_SHIFTS} most recent of {shiftHistory.length} days on record.
            </p>
          )}
        </div>
      )}

      {/* Roster — the per-day pattern, because days can genuinely differ now.
          One "Hours 08:00 – 16:00" line would hide a Tuesday that starts at
          nine. For a domiciliary service these same rows are availability, so
          no break or paid hours are shown. */}
      {roster && (
        <div className={`bg-white dark:bg-[#262b33] rounded-2xl border border-[#E2E8F0] dark:border-gray-800 shadow-sm ${cardPad}`}>
          <div className={`flex items-center justify-between gap-2 ${dense ? 'mb-4' : 'mb-5'}`}>
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center shrink-0">
                <Clock className="w-4 h-4 text-[#2563EB]" />
              </div>
              <h3 className="text-[15px] font-bold text-[#1E293B] dark:text-gray-200">
                {isAvailabilityPattern ? 'Availability' : 'Work Roster'}
              </h3>
            </div>
            {!isAvailabilityPattern && paidHoursPerWeek > 0 && (
              <span className="shrink-0 text-[13px] font-bold text-[#2563EB]">
                {paidHoursPerWeek} h / week
              </span>
            )}
          </div>

          <div className={`grid grid-cols-1 ${dense ? 'gap-y-3.5' : 'sm:grid-cols-2 gap-x-8 gap-y-4'} mb-4`}>
            {!isAvailabilityPattern && <Field label="Shift" value={roster.shift_name} />}
            <Field
              label="Contracted hours"
              value={roster.contracted_hours == null ? null : `${Number(roster.contracted_hours)} h / week`}
            />
          </div>

          {weekPattern.length > 0 ? (
            <div className="space-y-1.5">
              {weekPattern.map((entry) => (
                <div
                  key={entry.day}
                  className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg ${entry.on
                    ? 'bg-[#F8FAFC] dark:bg-[#20242c]'
                    : ''
                    }`}
                >
                  <span className={`text-[13px] font-semibold w-24 shrink-0 ${entry.on
                    ? 'text-[#1E293B] dark:text-gray-200'
                    : 'text-[#94A3B8] dark:text-gray-500'
                    }`}>
                    {entry.day}
                  </span>
                  {entry.on ? (
                    <span className="text-[13px] text-[#475569] dark:text-gray-300 font-medium text-right flex-1 min-w-0">
                      {String(entry.start).slice(0, 5)} – {String(entry.end).slice(0, 5)}
                      {!isAvailabilityPattern && Number(entry.break_minutes) > 0 && (
                        <span className="text-[#94A3B8] dark:text-gray-500">
                          {' '}· {entry.break_minutes}m break
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="text-[13px] text-[#94A3B8] dark:text-gray-500 text-right flex-1">
                      {isAvailabilityPattern ? 'Not available' : 'Day off'}
                    </span>
                  )}
                  {!isAvailabilityPattern && (
                    <span className={`text-[13px] font-bold w-14 text-right shrink-0 ${entry.on
                      ? 'text-[#1E293B] dark:text-gray-200'
                      : 'text-[#94A3B8] dark:text-gray-500'
                      }`}>
                      {paidHoursFor(entry).toFixed(1)} h
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            /* No pattern and no working days — nothing has been set yet. */
            <p className="text-[13px] text-[#94A3B8] dark:text-gray-500">
              No weekly pattern set yet.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Access — the three independent things this person was given: the Carer
 * toggle (a full mobile app), the app-access checkbox on its own (a view-only
 * app) and the module ticks (the dashboard). Set on the Access step of the
 * Add/Edit Staff form.
 *
 * Always shown, never hidden when empty like the other sections, because "no
 * dashboard access" is itself the answer a manager comes here for.
 */
const AccessCard = ({ isCarer, hasApp, canLogMeds, grantedMenus, cardPad }) => (
  <div className={`bg-white dark:bg-[#262b33] rounded-2xl border border-[#E2E8F0] dark:border-gray-800 shadow-sm ${cardPad}`}>
    <div className="flex items-center gap-2 mb-5">
      <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center shrink-0">
        <KeyRound className="w-4 h-4 text-[#2563EB]" />
      </div>
      <h3 className="text-[15px] font-bold text-[#1E293B] dark:text-gray-200">Access</h3>
    </div>

    {/* Mobile app — three states, because "can sign in" and "can record
        anything" are different questions now. */}
    <div className="flex items-start gap-3 mb-5">
      <div className={`w-8 h-8 rounded-lg shrink-0 flex items-center justify-center ${hasApp
        ? 'bg-purple-50 dark:bg-purple-900/20'
        : 'bg-[#F1F5F9] dark:bg-[#31373f]'
        }`}>
        <Smartphone className={`w-4 h-4 ${hasApp ? 'text-purple-600 dark:text-purple-400' : 'text-[#94A3B8] dark:text-gray-500'}`} />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold text-[#94A3B8] dark:text-gray-500 uppercase tracking-wider mb-1">Mobile app</p>
        {isCarer ? (
          <>
            <p className="text-[14px] font-medium text-[#1E293B] dark:text-gray-200 flex items-center gap-1.5">
              <Check className="w-4 h-4 text-emerald-600 shrink-0" strokeWidth={3} /> Carer — full access
            </p>
            <p className="text-[12px] text-[#64748B] dark:text-gray-400 mt-1 leading-relaxed">
              {canLogMeds
                ? 'Can log medication doses.'
                : 'Medication: view only — cannot log doses.'}
            </p>
          </>
        ) : hasApp ? (
          <>
            <p className="text-[14px] font-medium text-[#1E293B] dark:text-gray-200 flex items-center gap-1.5">
              <Eye className="w-4 h-4 text-[#2563EB] shrink-0" strokeWidth={2.5} /> View-only access
            </p>
            <p className="text-[12px] text-[#64748B] dark:text-gray-400 mt-1 leading-relaxed">
              Can sign in and see residents, care plans and the rota. Cannot
              clock in or record care notes, vitals or incidents.
            </p>
          </>
        ) : (
          <p className="text-[14px] font-medium text-[#1E293B] dark:text-gray-200 flex items-center gap-1.5">
            <Minus className="w-4 h-4 text-[#94A3B8] shrink-0" strokeWidth={3} /> No app access
          </p>
        )}
      </div>
    </div>

    {/* Dashboard modules */}
    <div className="flex items-start gap-3">
      <div className={`w-8 h-8 rounded-lg shrink-0 flex items-center justify-center ${grantedMenus.length
        ? 'bg-blue-50 dark:bg-blue-900/20'
        : 'bg-[#F1F5F9] dark:bg-[#31373f]'
        }`}>
        <Monitor className={`w-4 h-4 ${grantedMenus.length ? 'text-[#2563EB]' : 'text-[#94A3B8] dark:text-gray-500'}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-semibold text-[#94A3B8] dark:text-gray-500 uppercase tracking-wider mb-1">
          Dashboard modules{grantedMenus.length ? ` (${grantedMenus.length})` : ''}
        </p>
        {grantedMenus.length === 0 ? (
          <p className="text-[14px] font-medium text-[#1E293B] dark:text-gray-200 flex items-center gap-1.5">
            <Minus className="w-4 h-4 text-[#94A3B8] shrink-0" strokeWidth={3} /> No dashboard access
          </p>
        ) : (
          <div className="flex flex-wrap gap-2 mt-1.5">
            {grantedMenus.map((menu) => (
              <span
                key={menu.id}
                title={menu.code}
                className="px-2.5 py-1 rounded-lg bg-[#EFF6FF] dark:bg-blue-900/20 text-[#1D4ED8] dark:text-blue-400 border border-[#DBEAFE] dark:border-blue-900/50 text-[12px] font-medium"
              >
                {menu.name}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);

export default StaffDetails;
